/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";

import styles from "./HomeNetworkIndicator.module.css";

interface NetInfo {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener?: (type: "change", cb: () => void) => void;
  removeEventListener?: (type: "change", cb: () => void) => void;
}

type Level = "excellent" | "good" | "fair" | "poor" | "offline";

function readConnection(): NetInfo | undefined {
  return (navigator as unknown as { connection?: NetInfo }).connection;
}

function levelFromInfo(info: NetInfo | undefined, online: boolean): Level {
  if (!online) return "offline";
  if (!info) return "good";
  if (info.effectiveType) {
    switch (info.effectiveType) {
      case "4g":
        return "excellent";
      case "3g":
        return "fair";
      default:
        return "poor";
    }
  }
  if (typeof info.downlink === "number") {
    if (info.downlink >= 10) return "excellent";
    if (info.downlink >= 3) return "good";
    if (info.downlink >= 1) return "fair";
    return "poor";
  }
  return "good";
}

function label(info: NetInfo | undefined): string {
  if (!info?.effectiveType) {
    if (typeof info?.downlink === "number") return `${info.downlink.toFixed(0)} Mb`;
    return "Online";
  }
  const map: Record<string, string> = {
    "slow-2g": "Slow 2G",
    "2g": "2G",
    "3g": "3G",
    "4g": "4G / WiFi",
  };
  return map[info.effectiveType] ?? "Online";
}

function barsFor(level: Level): number {
  switch (level) {
    case "excellent":
      return 4;
    case "good":
      return 3;
    case "fair":
      return 2;
    case "poor":
      return 1;
    default:
      return 0;
  }
}

export const HomeNetworkIndicator: FC = () => {
  const [info, setInfo] = useState<NetInfo | undefined>(readConnection);
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const update = (): void => setInfo(readConnection());
    const conn = readConnection();
    conn?.addEventListener?.("change", update);
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      conn?.removeEventListener?.("change", update);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const level = levelFromInfo(info, online);
  const bars = barsFor(level);

  const speed =
    typeof info?.downlink === "number"
      ? `${info.downlink.toFixed(1)} Mb`
      : "";
  const ping =
    typeof info?.rtt === "number" && info.rtt > 0 ? ` · ${info.rtt}ms` : "";

  return (
    <div
      className={styles.network}
      data-level={level}
      title="Your internet connection"
    >
      <div className={styles.bars}>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={styles.bar}
            data-active={i < bars}
          />
        ))}
      </div>
      <span className={styles.label}>
        {level === "offline" ? "Offline" : label(info)}
        {!online ? "" : ` ${speed}${ping}`}
      </span>
    </div>
  );
};
